/* eslint-disable consistent-return, react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { Title } from '../blocks';
import Blocks from '../blocks/Blocks';
import JobListing from '../cards/JobListing';
import CareerPageHeader from '../partials/CareerPageHeader';
import CareerSection from '../partials/CareerSection';

import styles from '../../../styles/pages/career.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
    className: PropTypes.string,
};

const defaultProps = {
    className: null,
};

const CareerPage = ({ page, className }) => {
    const intl = useIntl();

    const jobsRef = useRef(null);
    const spontaneousRef = useRef(null);
    const internshipRef = useRef(null);
    const refs = [jobsRef, spontaneousRef, internshipRef];

    const {
        blocks = null,
        title = null,
        jobListings = [],
        careerSections: sections = [],
    } = page || {};

    const normalJobListings = (jobListings || []).filter(
        (listing) => listing.handle !== 'spontaneous',
    );
    const spontaneousJobListings = (jobListings || []).filter(
        (listing) => listing.handle === 'spontaneous',
    );

    const withJobListings = normalJobListings !== null && normalJobListings.length > 0;
    const withSpontaneousJobListings =
        spontaneousJobListings !== null && spontaneousJobListings.length > 0;
    const withSections = sections !== null && sections.length > 0;

    const anchors = [
        {
            id: 'joblistings',
            label: <FormattedMessage defaultMessage="Job listings" description="Button label" />,
            color: '#00ff89',
            active: withJobListings,
        },
        {
            id: 'spontaneous',
            label: (
                <FormattedMessage
                    defaultMessage="Spontaneous applications"
                    description="Button label"
                />
            ),
            color: '#f769ba',
            active: withSpontaneousJobListings,
        },
        {
            id: 'internship',
            label: (
                <FormattedMessage defaultMessage="Internship program" description="Button label" />
            ),
            color: '#fcd732',
            active: withSections,
        },
    ];

    const onClickAnchor = useCallback(
        (id) => {
            const targetRef = refs.find(
                (ref) => ref !== null && ref.current !== null && ref.current.id === id,
            );
            if (targetRef && targetRef.current !== null) {
                targetRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        },
        [refs],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <CareerPageHeader title={title} anchors={anchors} onClick={onClickAnchor} />
            <div className={styles.content}>
                {/* heading */}
                {blocks !== null ? (
                    <Blocks
                        className={styles.blocks}
                        blocks={blocks.map((block) => {
                            const { type } = block;
                            return type === 'gallery'
                                ? {
                                      ...block,
                                      imageClassName: styles.gallery,
                                  }
                                : block;
                        })}
                        isContainer
                        typeClassName={{
                            title: styles.title,
                        }}
                    />
                ) : null}
                {withJobListings ? (
                    <div
                        ref={jobsRef}
                        id="joblistings"
                        className={classNames([
                            styles.jobListings,
                            styles.first,
                            { [styles.last]: !withSpontaneousJobListings },
                        ])}
                    >
                        <Title
                            size="medium"
                            body={intl.formatMessage({
                                defaultMessage: 'Open listings',
                                description: 'Job listings title',
                            })}
                            isContainer
                            className={styles.title}
                        />
                        {(jobListings || []).map((jobListing, idx) => {
                            if (jobListing.handle === 'spontaneous') {
                                return;
                            }
                            return (
                                <JobListing
                                    key={`job-listing-${idx + 1}`}
                                    className={styles.jobListing}
                                    item={jobListing}
                                />
                            );
                        })}
                    </div>
                ) : null}
                {withSpontaneousJobListings ? (
                    <div
                        ref={spontaneousRef}
                        id="spontaneous"
                        className={classNames([styles.jobListings, styles.last])}
                    >
                        <Title
                            size="medium"
                            body={intl.formatMessage({
                                defaultMessage: 'Spontaneous applications',
                                description: 'Job listings title',
                            })}
                            isContainer
                            className={styles.title}
                        />
                        {(spontaneousJobListings || []).map((jobListing, idx) => {
                            if (jobListing.handle !== 'spontaneous') {
                                return;
                            }
                            return (
                                <JobListing
                                    key={`job-listing-${idx + 1}`}
                                    className={styles.jobListing}
                                    item={jobListing}
                                />
                            );
                        })}
                    </div>
                ) : null}
                {withSections ? (
                    <div
                        ref={internshipRef}
                        id="internship"
                        className={classNames({
                            [styles.withMargin]: !withSpontaneousJobListings && !withJobListings,
                        })}
                    >
                        {(sections || []).map((section, idx) => (
                            <CareerSection key={`career-section-${idx + 1}`} item={section} />
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

CareerPage.propTypes = propTypes;
CareerPage.defaultProps = defaultProps;

export default CareerPage;
